import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Star, Quote, Loader2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { fadeUp, stagger, viewport, containerViewport } from "../lib/motion";

export const Section = ({ children, className = "", id, wash = false, testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={`az-section ${wash ? "paper-wash" : "bg-[var(--paper)]"} ${className}`}
  >
    <div className="az-container">{children}</div>
  </section>
);

export const SectionHead = ({ eyebrow, title, subtitle, cta, ctaLabel, align = "left" }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={viewport}
    className={`mb-10 md:mb-14 flex flex-col gap-4 ${
      align === "center" ? "items-center text-center" : ""
    } md:flex-row md:items-end md:justify-between`}
  >
    <div className={align === "center" ? "max-w-2xl" : "max-w-2xl"}>
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.1] text-[var(--ink)]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-[0.95rem] sm:text-base leading-relaxed text-[var(--muted-ink)]">
          {subtitle}
        </p>
      )}
    </div>
    {cta && ctaLabel && (
      <Link
        to={cta}
        data-testid="section-cta-link"
        className="group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-[var(--brass-2)] hover:text-[var(--ink)]"
      >
        {ctaLabel}
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </Link>
    )}
  </motion.div>
);

export const Grid = ({ children, cols = 3, className = "" }) => {
  const map = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4",
  };
  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      whileInView="show"
      viewport={containerViewport}
      className={`grid grid-cols-1 gap-6 lg:gap-8 ${map[cols]} ${className}`}
    >
      {children}
    </motion.div>
  );
};

export const Img = ({ src, alt, className = "", ratio = "4/3", eager = false }) => (
  <div className={`az-media relative bg-[var(--paper-2)] ${className}`} style={{ aspectRatio: ratio }}>
    {src ? (
      <img
        src={src}
        alt={alt || ""}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        className="img-cover"
      />
    ) : null}
  </div>
);

export const EditorialCard = ({ to, image, title, subtitle, meta, tall = false, testId }) => (
  <motion.div variants={fadeUp}>
    <Link to={to} data-testid={testId} className="az-card group block h-full">
      <Img src={image} alt={title} ratio={tall ? "3/4" : "4/3"} />
      <div className="p-5 sm:p-6">
        {meta && <p className="eyebrow mb-2">{meta}</p>}
        <h3 className="font-display text-[1.45rem] leading-snug text-[var(--ink)]">{title}</h3>
        {subtitle && (
          <p className="mt-2 text-sm leading-relaxed text-[var(--muted-ink)] line-clamp-3">{subtitle}</p>
        )}
      </div>
    </Link>
  </motion.div>
);

export const OverlayCard = ({ to, image, title, subtitle, meta, testId }) => (
  <motion.div variants={fadeUp}>
    <Link
      to={to}
      data-testid={testId}
      className="group relative block overflow-hidden rounded-2xl bg-[var(--navy)]"
    >
      <div className="az-media" style={{ aspectRatio: "3/4" }}>
        {image && <img src={image} alt={title} loading="lazy" className="img-cover" />}
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,16,22,0.88)] via-[rgba(10,16,22,0.15)] to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
        {meta && (
          <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[#E4CFA9]">
            {meta}
          </p>
        )}
        <h3 className="font-display text-[1.5rem] leading-tight text-white">{title}</h3>
        {subtitle && <p className="mt-1.5 text-sm text-white/75 line-clamp-2">{subtitle}</p>}
      </div>
    </Link>
  </motion.div>
);

export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-6">
    <ol className="flex flex-wrap items-center gap-1.5 text-xs text-[var(--muted-ink)]">
      {items.map((item, i) => (
        <li key={item.to || item.label || i} className="flex items-center gap-1.5">
          {i > 0 && <span className="opacity-40">/</span>}
          {item.to ? (
            <Link to={item.to} className="hover:text-[var(--brass-2)]">
              {item.label}
            </Link>
          ) : (
            <span className="text-[var(--ink)]">{item.label}</span>
          )}
        </li>
      ))}
    </ol>
  </nav>
);

export const RichText = ({ text, className = "" }) => {
  if (!text) return null;
  const blocks = String(text).split(/\n\n+/);
  return (
    <div className={`prose-az ${className}`}>
      {blocks.map((block, i) => {
        const b = block.trim();
        const key = blockKey(b, i);
        if (b.startsWith("## ")) return <h2 key={key}>{inline(b.slice(3))}</h2>;
        if (b.startsWith("### ")) return <h3 key={key}>{inline(b.slice(4))}</h3>;
        if (/^[-*]\s/m.test(b) && b.split("\n").every((l) => /^[-*]\s/.test(l.trim()))) {
          return (
            <ul key={key}>
              {b.split("\n").map((l, j) => {
                const item = l.trim().replace(/^[-*]\s/, "");
                return <li key={blockKey(item, j)}>{inline(item)}</li>;
              })}
            </ul>
          );
        }
        return <p key={key}>{inline(b)}</p>;
      })}
    </div>
  );
};

/**
 * Key for a static markdown fragment: its text plus position, so identical
 * paragraphs in one document still get distinct keys.
 */
const blockKey = (text, position) => `${position}:${text.slice(0, 40)}`;

function inline(str) {
  const parts = String(str).split(/(\[[^\]]+\]\(\/[^\s)]*\)|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    const key = blockKey(p, i);
    const link = p.match(/^\[([^\]]+)\]\((\/(?!\/)[^\s)]*)\)$/);
    if (link && !link[2].includes("\\")) return (
      <Link key={key} to={link[2]} data-testid={`editorial-link-${link[2].replace(/[^a-z0-9]+/gi, "-")}-${i}`}
        className="text-[var(--brass-2)] underline underline-offset-4 hover:text-[var(--ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--brass)]">
        {link[1]}
      </Link>
    );
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={key}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) return <em key={key}>{p.slice(1, -1)}</em>;
    return p;
  });
}

export const FaqList = ({ faqs, testId = "faq-accordion" }) => {
  if (!faqs?.length) return null;
  return (
    <Accordion type="single" collapsible className="w-full" data-testid={testId}>
      {faqs.map((f, i) => (
        <AccordionItem key={f.question || f.title || i} value={`faq-${i}`} className="border-b border-[var(--line)]">
          <AccordionTrigger
            data-testid={`faq-trigger-${i}`}
            className="py-5 text-left font-display text-lg text-[var(--ink)] hover:no-underline"
          >
            {f.question || f.title}
          </AccordionTrigger>
          <AccordionContent className="pb-5 text-[0.95rem] leading-relaxed text-[var(--muted-ink)]">
            {f.answer}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export const Reviews = ({ reviews, testId = "reviews" }) => {
  if (!reviews?.length) return null;
  return (
    <Grid cols={reviews.length > 2 ? 3 : 2} className="" >
      {reviews.map((r, i) => (
        <motion.figure
          key={`${r.name || "review"}-${i}`}
          variants={fadeUp}
          data-testid={`${testId}-item-${i}`}
          className="flex h-full flex-col rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-6"
        >
          <Quote className="mb-4 h-5 w-5 text-[var(--brass)]" />
          <div className="mb-3 flex gap-0.5">
            {Array.from({ length: r.rating || 5 }, (_, k) => `star-${k + 1}`).map((starId) => (
              <Star key={starId} className="h-3.5 w-3.5 fill-[var(--brass)] text-[var(--brass)]" />
            ))}
          </div>
          <blockquote className="flex-1 text-[0.95rem] leading-relaxed text-[var(--ink-2)]">
            {r.quote}
          </blockquote>
          <figcaption className="mt-5 border-t border-[var(--line)] pt-4">
            <p className="text-sm font-semibold text-[var(--ink)]">{r.name}</p>
            <p className="text-xs text-[var(--muted-ink)]">
              {[r.location, r.trip].filter(Boolean).join(" · ")}
            </p>
          </figcaption>
        </motion.figure>
      ))}
    </Grid>
  );
};

export const Gallery = ({ images, alt = "", testId = "gallery" }) => {
  if (!images?.length) return null;
  return (
    <div data-testid={testId} className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
      {images.slice(0, 8).map((src, i) => (
        <div
          key={src || i}
          data-testid={`${testId}-image-${i}`}
          className={`az-media overflow-hidden rounded-xl ${i === 0 ? "col-span-2 row-span-2" : ""}`}
          style={{ aspectRatio: i === 0 ? "1/1" : "1/1" }}
        >
          <img src={src} alt={`${alt} ${i + 1}`} loading="lazy" className="img-cover" />
        </div>
      ))}
    </div>
  );
};

export const PageHero = ({ image, eyebrow, title, subtitle, children, height = "tall", focus }) => (
  <header
    data-testid="page-hero"
    className={`grain relative isolate flex items-end overflow-hidden bg-[var(--navy)] ${
      height === "tall" ? "min-h-[62vh] md:min-h-[70vh]" : "min-h-[46vh] md:min-h-[52vh]"
    }`}
  >
    {image && (
      <img
        src={image}
        alt={title}
        fetchPriority="high"
        className="absolute inset-0 img-cover"
        style={focus ? { objectPosition: focus } : undefined}
        loading="eager"
      />
    )}
    <div className="absolute inset-0 hero-scrim" />
    <div className="az-container relative w-full pb-12 pt-32 md:pb-20">
      {eyebrow && (
        <p className="mb-4 text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-[#E4CFA9]">
          {eyebrow}
        </p>
      )}
      <h1
        data-testid="page-title"
        className="max-w-4xl font-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-[4rem]"
      >
        {title}
      </h1>
      {subtitle && (
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">{subtitle}</p>
      )}
      {children}
    </div>
  </header>
);

export const Loading = ({ label = "Loading" }) => (
  <div
    data-testid="loading-state"
    className="flex min-h-[60vh] flex-col items-center justify-center gap-3 bg-[var(--paper)]"
  >
    <Loader2 className="h-6 w-6 animate-spin text-[var(--brass)]" />
    <p className="text-sm text-[var(--muted-ink)]">{label}…</p>
  </div>
);

export const Empty = ({ title, body, testId = "empty-state" }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-dashed border-[var(--line-strong)] bg-[var(--surface-warm)] p-12 text-center"
  >
    <h3 className="font-display text-2xl text-[var(--ink)]">{title}</h3>
    {body && <p className="mt-2 text-sm text-[var(--muted-ink)]">{body}</p>}
  </div>
);

export const Pill = ({ children, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
      active
        ? "border-[var(--brass)] bg-[var(--brass)] text-white"
        : "border-[var(--line-strong)] bg-[var(--surface)] text-[var(--ink-2)] hover:border-[var(--brass)] hover:text-[var(--brass-2)]"
    }`}
  >
    {children}
  </button>
);

export const FactRow = ({ label, value, testId }) =>
  value ? (
    <div className="flex flex-col gap-1 border-b border-[var(--line)] py-3.5 sm:flex-row sm:items-baseline sm:gap-6">
      <dt className="eyebrow shrink-0 sm:w-44">{label}</dt>
      <dd data-testid={testId} className="text-[0.95rem] leading-relaxed text-[var(--ink-2)]">
        {value}
      </dd>
    </div>
  ) : null;
